import React, { memo, useState } from "react";
import { Field, Label } from "@headlessui/react";
import TagItem from "./TagItem";
import { _t } from "core/language";
import { TagProps, Tag } from "./TagList.d";
import TagSkeleton from "./TagSkeleton";
import TagListExpandLimitLine from "./TagListExpandLimitLine";

const TagList: React.FC<TagProps> = ({
  label,
  options,
  value = [],
  handleTagClick,
  onChange,
  expandLimit,
  expandLine,
  className,
  labelClassName,
  listClassName,
  itemClassName,
  itemInline,
  loading,
  activeClasses,
  inactiveClasses,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const _handleTagClick = (item: Tag) => {
    handleTagClick?.(item);
    if (value.includes(item.id)) {
      const _value = value.filter((e: string) => e !== item.id);
      onChange?.(
        _value,
        options.filter((e: Tag) => _value.includes(e.id))
      );
    } else {
      onChange?.(
        [...value, item.id],
        [...options.filter((e: Tag) => value.includes(e.id)), item]
      );
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const visibleTags =
    isExpanded && expandLimit ? options : options.slice(0, expandLimit);

  const renderExpandLimit = <>
    {(visibleTags || []).map((tag) => (
      <TagItem
        key={tag.id}
        text={tag.text}
        isActive={value.includes(tag.id)}
        onClick={() => _handleTagClick(tag)}
        className={itemClassName}
        activeClasses={activeClasses}
        inactiveClasses={inactiveClasses}
      />
    ))}
    {(expandLimit && options.length > expandLimit) && (
      <button
        type="button"
        className={`
          gap-1 self-stretch px-3.5 py-[4px] my-auto text-[13px]
          bg-white border border-solid border-stone-300 rounded-[50px] 
          hover:bg-gray-50 transition-colors duration-200 min-h-[32px] tracking-tighter flex items-center
        `}
        onClick={toggleExpand}
        aria-expanded={isExpanded}
      >
        <span className="text-[13px] text-[#777777]">
          {isExpanded ? _t("main.tag.close") : _t("main.tag.open")}
        </span>{" "}
        <span className="text-[8px] flex items-center pt-[5px] h-[10px]">{isExpanded ? "▲" : "▼"}</span>
      </button>
    )}
  </>

  return (
    <Field className={className}>
      {label && (
        <Label className={`text-dark text-[13px] ${labelClassName}`}>
          {_t(label)}
        </Label>
      )}
      <nav
        className={`
          flex gap-2.5 items-center
          text-sm font-medium tracking-tighter
          ${listClassName}
          ${itemInline ? "overflow-x-auto no-scrollbar" : "flex-wrap"}
        `}
      >
        {loading ? (
          <TagSkeleton />
        ) : (
          expandLine ? <TagListExpandLimitLine
            label={label}
            options={options}
            value={value}
            handleTagClick={_handleTagClick}
            onChange={onChange}
            expandLimit={expandLimit}
            expandLine={expandLine}
            className={className}
            labelClassName={labelClassName}
            listClassName={listClassName}
            itemClassName={itemClassName}
            itemInline={itemInline}
            loading={loading}
            activeClasses={activeClasses}
            inactiveClasses={inactiveClasses}
          /> : renderExpandLimit
        )}
      </nav>
    </Field>
  );
};

export default memo(TagList);
